import { Box, Pagination } from '@mui/material'
import { useEffect, useState } from 'react'
import ApiClient from '../backend/ApiClient'
import { IClaimFilter } from '../definitions/Claims'
import { IPagination } from '../definitions/Common'
import Helper from '../helpers/Helper'
import TimeHelper from '../helpers/TimeHelper'
import { useAppSelector } from '../store/Hooks'
import ClaimLinesTable from './controls/ClaimLinesTable'
import SmartTable from './controls/table/SmartTable'
import useQuery, { buildQueryKey } from './hooks/useQuery'
import Tile from './layout/Tile'
import { SmartTableUtilities } from './controls/table'

interface P {
    kpiIsLoading?: boolean
    totalClaims: number
    contractId: number
    filter?: IClaimFilter
}

function ClaimsTable({ totalClaims, filter, contractId, kpiIsLoading }: P) {
    const time = useAppSelector(s => s.app.timeSettings ?? TimeHelper.timeSettings())
    const [pagination, setPagination] = useState<IPagination>({ page: 0, batch: 20 })

    const { status: claimsBreakdownStatus, response: claimsBreakdown } = useQuery({
        queryKey: buildQueryKey('claims-breakdown', contractId, TimeHelper.stringify(time), pagination.page, pagination.batch, Helper.generateKeyOfObject(filter)),
        queryFn: () => ApiClient.getContractClaims(contractId, time, pagination, filter),
        cache: true
    })

    useEffect(() => {
        setPagination({ ...pagination, page: 0 })
    }, [filter])

    const isLoading = kpiIsLoading || claimsBreakdownStatus === 'loading'
    const totalPage = Math.floor((totalClaims ?? 1) / pagination.batch)
    const lastPageHaveValues = (totalClaims ?? 1) % pagination.batch != 0

    return (
        <Tile.Container>
            <Tile.Row spacing={3} style={{ overflowY: 'auto', display: 'table' }}>
                <SmartTable
                    isLoading={isLoading}
                    sticky
                    columns={[
                        { id: 'datetime', label: 'Datetime', width: 200 },
                        { id: 'provider', label: 'Provider' },
                        { id: 'status', label: 'Status' },
                        { id: 'isBadClaim', label: 'Is Bad Claim' },
                        { id: 'totalclaimlines', label: 'N° Claimlines', numeric: true },
                        { id: 'totalpayment', label: 'TotalPayment', numeric: true }
                    ]}
                    datasource={{
                        rows: (claimsBreakdown?.claims ?? []).map((x, i) => {
                            return SmartTableUtilities.toRow([
                                { value: x?.datetime },
                                { value: x?.provider?.name },
                                { value: x?.status },
                                { value: x?.isBadClaim?.toString() },
                                { value: x?.totalclaimlines },
                                { value: x?.totalPayment }
                            ], i, <ClaimLinesTable key={x.id} claim={x} contractId={contractId} />)
                        })
                    }}
                />
            </Tile.Row>
            <Box display='flex' justifyContent='flex-end'>
                <Pagination
                    count={totalPage + (lastPageHaveValues ? 1 : 0)}
                    defaultPage={(pagination.page + 1)}
                    color='primary'
                    disabled={claimsBreakdownStatus !== 'success'}
                    onChange={(_, value) => setPagination(p => {
                        return { batch: p.batch, page: value - 1 }
                    })}
                />
            </Box>
        </Tile.Container>
    )
}

export default ClaimsTable
